import { supabase } from "@/integrations/supabase/client";
import type {
  BookingPaymentFlow,
  ManagedBookingPolicy,
  ManagedCourt,
} from "@/types/services/api-contracts";

import { callEdgeFunction } from "./_core/edge-call";

export const courtService = {
  async create(data: {
    venue_id: string;
    name: string;
    sport_id?: string;
    surface_type?: string;
    indoor?: boolean;
    has_lighting?: boolean;
    capacity?: number;
  }): Promise<Record<string, unknown>> {
    return callEdgeFunction("manage-court", "create", { body: data });
  },

  async update(data: {
    court_id: string;
    name?: string;
    sport_id?: string;
    surface_type?: string;
    indoor?: boolean;
    has_lighting?: boolean;
    capacity?: number;
    active?: boolean;
  }): Promise<Record<string, unknown>> {
    return callEdgeFunction("manage-court", "update", { method: "PUT", body: data });
  },

  async softDelete(courtId: string): Promise<Record<string, unknown>> {
    return callEdgeFunction("manage-court", "delete", {
      method: "DELETE",
      params: { court_id: courtId },
    });
  },

  async listByVenue(venueId: string): Promise<ManagedCourt[]> {
    const { data, error } = await supabase
      .from("courts")
      .select(`
        *,
        sports(name, icon),
        court_booking_policies(
          court_id,
          slot_interval_minutes,
          min_duration_minutes,
          max_duration_minutes,
          buffer_before_minutes,
          buffer_after_minutes,
          pending_expiration_minutes,
          lead_time_minutes,
          advance_window_days,
          allow_manual_override,
          payment_flow,
          available_payment_flows,
          prebooking_hold_minutes
        ),
        pricing_rules(
          id,
          day_of_week,
          start_time,
          end_time,
          price_cents,
          is_peak,
          active,
          label,
          priority
        )
      `)
      .eq("venue_id", venueId)
      .is("deleted_at", null)
      .order("name");
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map((court) => {
      const bookingPolicies = Array.isArray(court.court_booking_policies)
        ? court.court_booking_policies
        : court.court_booking_policies
          ? [court.court_booking_policies]
          : [];

      const bookingPolicyRecord = (bookingPolicies[0] ?? null) as ManagedBookingPolicy | null;
      const bookingPolicy = bookingPolicyRecord
        ? {
            ...bookingPolicyRecord,
            available_payment_flows:
              Array.isArray(bookingPolicyRecord.available_payment_flows) && bookingPolicyRecord.available_payment_flows.length
                ? bookingPolicyRecord.available_payment_flows
                : [bookingPolicyRecord.payment_flow ?? "match_checkout"],
          }
        : null;

      return {
        ...(court as unknown as ManagedCourt),
        booking_policy: bookingPolicy,
      };
    }) as ManagedCourt[];
  },

  async upsertBookingPolicy(data: {
    court_id: string;
    slot_interval_minutes: number;
    min_duration_minutes: number;
    max_duration_minutes: number;
    buffer_before_minutes: number;
    buffer_after_minutes: number;
    lead_time_minutes: number;
    advance_window_days: number;
    pending_expiration_minutes?: number;
    allow_manual_override?: boolean;
    payment_flow?: BookingPaymentFlow;
    available_payment_flows?: BookingPaymentFlow[];
    prebooking_hold_minutes?: number;
  }): Promise<ManagedBookingPolicy> {
    const payload = {
      court_id: data.court_id,
      slot_interval_minutes: data.slot_interval_minutes,
      min_duration_minutes: data.min_duration_minutes,
      max_duration_minutes: data.max_duration_minutes,
      buffer_before_minutes: data.buffer_before_minutes,
      buffer_after_minutes: data.buffer_after_minutes,
      lead_time_minutes: data.lead_time_minutes,
      advance_window_days: data.advance_window_days,
      pending_expiration_minutes: data.pending_expiration_minutes ?? 10,
      allow_manual_override: data.allow_manual_override ?? true,
      payment_flow: data.payment_flow ?? "match_checkout",
      available_payment_flows: data.available_payment_flows ?? [data.payment_flow ?? "match_checkout"],
      prebooking_hold_minutes: data.prebooking_hold_minutes ?? 180,
    };

    const { data: policy, error } = await supabase
      .from("court_booking_policies")
      .upsert(payload, { onConflict: "court_id" })
      .select(`
        court_id,
        slot_interval_minutes,
        min_duration_minutes,
        max_duration_minutes,
        buffer_before_minutes,
        buffer_after_minutes,
        pending_expiration_minutes,
        lead_time_minutes,
        advance_window_days,
        allow_manual_override,
        payment_flow,
        available_payment_flows,
        prebooking_hold_minutes
      `)
      .single();

    if (error) throw error;
    if (!policy) {
      throw new Error("Nao foi possivel carregar a politica de reserva da quadra.");
    }
    return policy as ManagedBookingPolicy;
  },
};

